import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { adminApi, type AdminStats } from '../../services/adminApi';

interface StatTile {
  key: keyof AdminStats;
  label: string;
}

const STAT_TILES: StatTile[] = [
  { key: 'totalUsers', label: 'Users' },
  { key: 'totalRetailers', label: 'Retailers' },
  { key: 'activeRetailers', label: 'Active retailers' },
  { key: 'activeListings', label: 'Active listings' },
  { key: 'pendingReviewItems', label: 'Pending review items' },
  { key: 'activeAlerts', label: 'Active alerts' },
];

interface AreaLink {
  to: string;
  title: string;
  description: string;
  ready: boolean;
}

// The five admin sub-areas tracked in GitHub #12. Review Queue ships today;
// the rest link to their (not-yet-built) routes and are flagged "Coming soon".
const AREA_LINKS: AreaLink[] = [
  {
    to: '/admin/review-queue',
    title: 'Moderation: review queue',
    description: 'Triage low-confidence normalizer output and merge duplicates.',
    ready: true,
  },
  {
    to: '/admin/users',
    title: 'User management',
    description: 'View accounts, roles, and account status.',
    ready: true,
  },
  {
    to: '/admin/advertisers',
    title: 'Advertiser management',
    description: 'Review advertiser accounts, campaigns, and billing.',
    ready: true,
  },
  {
    to: '/admin/retailers',
    title: 'Retailer management',
    description: 'Trust score, active / degraded flags, and last-scrape time.',
    ready: true,
  },
  {
    to: '/admin/scrape-jobs',
    title: 'Scrape jobs',
    description: 'Read-only history of recent scrape runs and their counts.',
    ready: true,
  },
  {
    to: '/admin/scrape-health',
    title: 'Scrape health',
    description: 'Per-retailer scrape status, staleness, and listing counts.',
    ready: true,
  },
  {
    to: '/admin/scrape-errors',
    title: 'Scrape errors',
    description: 'Persisted crawl failures — why a vendor crawl failed, newest first.',
    ready: true,
  },
  {
    to: '/admin/alert-performance',
    title: 'Alert performance',
    description: 'Alerts evaluated / fired / suppressed and notifications sent.',
    ready: true,
  },
  {
    to: '/admin/audit-log',
    title: 'Audit log',
    description: 'Review admin and system actions with filters.',
    ready: true,
  },
  {
    to: '/admin/affiliates',
    title: 'Affiliate program',
    description: 'Manage affiliate programs, links, and commission reporting.',
    ready: true,
  },
];

/**
 * Admin landing / overview page.
 *
 * Surfaces safe aggregate counts (no PII) from `GET /api/admin/stats` and a
 * directory of the admin sub-areas. Auth is enforced upstream by AdminLayout
 * (redirects non-admins), matching the gating already used by the review-queue
 * pages.
 */
/** A labeled top-N count list for the analytics section (#212). */
function TopList({
  title,
  rows,
  empty,
}: {
  title: string;
  rows: Array<{ label: string; count: number }>;
  empty: string;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h3" component="h3" sx={{ fontSize: '1rem', fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {empty}
        </Typography>
      ) : (
        <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
          {rows.map((r) => (
            <Box component="li" key={r.label} sx={{ mb: 0.5 }}>
              <Typography component="span" variant="body2">
                {r.label}
              </Typography>{' '}
              <Typography component="span" variant="body2" color="text.secondary">
                ({r.count.toLocaleString()})
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}

export function AdminDashboardPage() {
  const query = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => adminApi.getStats(),
  });

  // Usage analytics (#212) — aggregate top-N lists over the last 7 days.
  const analytics = useQuery({
    queryKey: ['admin', 'analytics'],
    queryFn: () => adminApi.getAnalytics({ windowHours: 168, limit: 10 }),
  });

  return (
    <Box>
      <Typography variant="h1" sx={{ fontSize: '1.6rem', mb: 1 }}>
        Admin dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Aggregate, non-identifying counts and links to each admin area.
      </Typography>

      <Typography variant="h2" component="h2" sx={{ fontSize: '1.2rem', mb: 1.5 }}>
        Overview
      </Typography>

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading admin statistics" />
        </Box>
      )}

      {query.isError && (
        <Alert severity="error" role="alert">
          Failed to load statistics: {(query.error as Error).message}
        </Alert>
      )}

      {query.isSuccess && (
        <Box
          component="ul"
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(6, 1fr)',
            },
            gap: 2,
            listStyle: 'none',
            pl: 0,
            m: 0,
            mb: 4,
          }}
        >
          {STAT_TILES.map((tile) => (
            <Box key={tile.key} component="li">
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="h3" component="p" sx={{ fontSize: '1.8rem', fontWeight: 700 }}>
                  {query.data[tile.key].toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {tile.label}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      )}

      <Typography variant="h2" component="h2" sx={{ fontSize: '1.2rem', mb: 1.5 }}>
        Usage analytics
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Aggregate counts over the last 7 days. Searches are recorded only with analytics consent;
        clicks are consent-gated.
      </Typography>

      {analytics.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading usage analytics" />
        </Box>
      )}
      {analytics.isError && (
        <Alert severity="error" role="alert" sx={{ mb: 3 }}>
          Failed to load analytics: {(analytics.error as Error).message}
        </Alert>
      )}
      {analytics.isSuccess && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 4,
          }}
        >
          <TopList
            title="Top searches"
            empty="No searches recorded yet."
            rows={analytics.data.topSearches.map((s) => ({ label: s.query, count: s.count }))}
          />
          <TopList
            title="Popular cartridges"
            empty="No cartridge searches yet."
            rows={analytics.data.popularCalibers.map((c) => ({ label: c.caliber, count: c.count }))}
          />
          <TopList
            title="Top outbound clicks"
            empty="No outbound clicks yet."
            rows={analytics.data.topClickedRetailers.map((r) => ({
              label: r.retailerName ?? r.retailerId,
              count: r.count,
            }))}
          />
        </Box>
      )}

      <Typography variant="h2" component="h2" sx={{ fontSize: '1.2rem', mb: 1.5 }}>
        Areas
      </Typography>

      <Box
        component="ul"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2,
          listStyle: 'none',
          pl: 0,
          m: 0,
        }}
      >
        {AREA_LINKS.map((area) => (
          <Box key={area.to} component="li">
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardActionArea
                component={RouterLink}
                to={area.to}
                disabled={!area.ready}
                sx={{ height: '100%', alignItems: 'flex-start' }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <MuiLink component="span" variant="h3" sx={{ fontSize: '1.05rem' }}>
                      {area.title}
                    </MuiLink>
                    {!area.ready && <Chip label="Coming soon" size="small" />}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {area.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default AdminDashboardPage;
